/**
 * Financial Knowledge Translation Helper
 * Provides access to translated financial knowledge from i18n
 */

import i18n from '../i18n/config';

/**
 * Get financial knowledge for a concept in the current language
 * @param {string} concept - Concept name (e.g., 'vix', 'beta')
 * @param {string} lang - Optional language override
 * @returns {object|null} - Knowledge object or null if not found
 */
export function getFinancialKnowledge(concept, lang = null) {
    const currentLang = lang || i18n.language || 'en';
    const knowledge = i18n.t(`financialKnowledge.${concept}`, { 
        returnObjects: true, 
        lng: currentLang,
        defaultValue: null
    });
    return knowledge;
}

/**
 * Get interpretation for a concept value
 * @param {string} concept - Concept name
 * @param {number} value - Value to interpret
 * @param {string} lang - Optional language override
 * @returns {string} - Interpretation text
 */
export function getInterpretation(concept, value, lang = null) {
    const currentLang = lang || i18n.language || 'en';
    const interpretations = i18n.t(`financialKnowledge.${concept}.interpretation`, { 
        returnObjects: true,
        lng: currentLang,
        defaultValue: {}
    });
    
    if (!interpretations || typeof interpretations !== 'object') {
        return '';
    }
    
    // VIX interpretation
    if (concept === 'vix') {
        if (value < 12) return interpretations.veryLow || '';
        if (value < 15) return interpretations.low || '';
        if (value < 20) return interpretations.moderate || '';
        if (value < 25) return interpretations.elevated || '';
        if (value < 30) return interpretations.high || '';
        return interpretations.veryHigh || '';
    }
    
    // Beta interpretation
    if (concept === 'beta') {
        if (value < 0) return interpretations.negative || '';
        if (value < 0.5) return interpretations.low || '';
        if (value < 1.0) return interpretations.belowOne || '';
        if (value === 1.0) return interpretations.one || '';
        if (value < 1.5) return interpretations.aboveOne || '';
        return interpretations.high || '';
    }
    
    // Volatility interpretation
    if (concept === 'volatility') {
        if (value < 10) return interpretations.low || '';
        if (value < 15) return interpretations.moderate || '';
        if (value < 20) return interpretations.elevated || '';
        if (value < 30) return interpretations.high || '';
        return interpretations.veryHigh || '';
    }
    
    // Sharpe ratio interpretation
    if (concept === 'sharpe') {
        if (value < 0) return interpretations.negative || '';
        if (value < 1) return interpretations.poor || '';
        if (value < 2) return interpretations.good || '';
        if (value < 3) return interpretations.veryGood || '';
        return interpretations.excellent || '';
    }
    
    // Correlation interpretation
    if (concept === 'correlation') {
        if (value < -0.5) return interpretations.strongNegative || '';
        if (value < 0) return interpretations.negative || '';
        if (value < 0.3) return interpretations.lowPositive || '';
        if (value < 0.7) return interpretations.moderate || '';
        if (value < 0.9) return interpretations.high || '';
        return interpretations.veryHigh || '';
    }
    
    return '';
}

/**
 * Get a specific field from financial knowledge
 * @param {string} concept - Concept name
 * @param {string} field - Field name (e.g., 'what', 'calculation', 'relevance')
 * @param {string} lang - Optional language override
 * @returns {string} - Field value or empty string
 */
export function getFinancialKnowledgeField(concept, field, lang = null) {
    const knowledge = getFinancialKnowledge(concept, lang);
    if (!knowledge) return '';
    return knowledge[field] || '';
}
